<template>
  <BaseCard :className="tallChartCardClass">
    <div class="px-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
      <div class="flex flex-col">
        <div class="text-lg leading-none font-semibold break-words">{{ titleComputed }}</div>
        <!-- <div class="text-xs text-muted-foreground">{{ sector }}/{{ kpiName }}</div> -->
      </div>

      <div class="flex flex-row items-center gap-3 shrink-0">
        <BaseButton v-if="!readonly" size="sm" variant="outline" @click="isOpen = true">{{ editButtonLabel }}</BaseButton>
      </div>
    </div>

    <div class="px-6">
      <div v-if="isLoading" :class="['flex items-center justify-center', chartOuterClass]">
        <div class="text-muted-foreground">Carregando...</div>
      </div>
      <div v-else-if="error" class="text-sm text-destructive py-6">{{ error }}</div>
      <div
        v-else
        :class="[chartOuterClass, 'cursor-pointer outline-none focus-visible:ring-2 focus-visible:ring-ring rounded-md']"
        title="Clique para ampliar"
        role="button"
        tabindex="0"
        @click="openChartZoom"
        @keydown.enter.prevent="openChartZoom"
        @keydown.space.prevent="openChartZoom"
      >
        <ComboBarLineDualAxisChart v-if="isComboBarLineDualAxis" :chartJs="chartJs" />
        <VerticalGroupedBarChart v-else-if="isWeeklyVerticalChart" :chartJs="chartJs" />
        <HorizontalGroupedBarChart v-else :chartJs="chartJs" />
      </div>
    </div>

    <!-- BaseDialog fixa sm:max-w-lg; largura real via style inline para este modal apenas -->
    <BaseDialog
      v-if="!readonly"
      :open.sync="isOpen"
      contentClass="overflow-y-auto"
      :contentStyle="dialogContentStyle"
    >
      <template #header>
        <div class="flex flex-col gap-2">
          <div class="text-lg leading-none font-semibold">{{ titleComputed }}</div>
          <div class="text-xs text-muted-foreground">{{ modalSubtitle }}</div>
        </div>
      </template>

      <template #default>
        <div v-if="localPayload" class="mt-4 space-y-4">
          <div v-if="isConversationsWeekly" class="space-y-6">
            <div v-for="c in categories" :key="c.key" class="border rounded-lg overflow-hidden">
              <div class="bg-muted/30 px-3 py-2 font-semibold text-sm">{{ c.label }}</div>
              <div class="overflow-x-auto">
                <table class="min-w-full text-sm">
                  <thead>
                    <tr class="bg-muted/20">
                      <th class="text-left p-2 sticky left-0 bg-muted/20 z-10">Colaborador</th>
                      <th
                        v-for="(wl, wi) in weekColumnLabels"
                        :key="'w-' + c.key + '-' + wi"
                        class="text-center p-2 whitespace-nowrap min-w-[6rem]"
                      >
                        {{ wl }}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="s in seriesOrdered" :key="`${c.key}-${s.key}`" class="border-t">
                      <td class="p-2 font-medium sticky left-0 bg-background whitespace-nowrap z-10">{{ s.label }}</td>
                      <td v-for="wk in weekKeysForEdit" :key="`${c.key}-${s.key}-${wk}`" class="p-2 align-top text-center">
                        <div class="json-kpi-week-input-wrap">
                          <BaseInput
                            :value="String(getWeekValue(s.key, c.key, wk))"
                            className="text-center tabular-nums px-2"
                            @input="(v) => setWeekValue(s.key, c.key, wk, v)"
                          />
                        </div>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          <div v-else-if="isReembolsosWeekly" class="space-y-6">
            <div v-for="c in categories" :key="c.key" class="border rounded-lg overflow-hidden">
              <div class="bg-muted/30 px-3 py-2 font-semibold text-sm">{{ c.label }}</div>
              <div class="overflow-x-auto">
                <table class="min-w-full text-sm">
                  <thead>
                    <tr class="bg-muted/20">
                      <th class="text-left p-2 sticky left-0 bg-muted/20 z-10">Semana</th>
                      <th
                        v-for="s in seriesOrdered"
                        :key="'rh-' + c.key + '-' + s.key"
                        class="text-center p-2 whitespace-nowrap min-w-[8rem]"
                      >
                        {{ s.label }}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr
                      v-for="(wk, wi) in weekKeysForEdit"
                      :key="`${c.key}-w-${wk}`"
                      class="border-t"
                    >
                      <td class="p-2 font-medium sticky left-0 bg-background whitespace-nowrap z-10">
                        {{ weekColumnLabels[wi] }}
                      </td>
                      <td v-for="s in seriesOrdered" :key="`${c.key}-${wk}-${s.key}`" class="p-2 align-top text-center">
                        <div class="json-kpi-week-input-wrap">
                          <BaseInput
                            :value="String(getWeekValue(s.key, c.key, wk))"
                            className="text-center tabular-nums px-2"
                            @input="(v) => setWeekValue(s.key, c.key, wk, v)"
                          />
                        </div>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          <div v-else-if="isOperacoesComboWeekly" class="space-y-6">
            <div v-for="c in categories" :key="c.key" class="border rounded-lg overflow-hidden">
              <div class="bg-muted/30 px-3 py-2 font-semibold text-sm">{{ c.label }}</div>
              <div class="overflow-x-auto">
                <table class="min-w-full text-sm">
                  <thead>
                    <tr class="bg-muted/20">
                      <th class="text-left p-2 sticky left-0 bg-muted/20 z-10">Semana</th>
                      <th
                        v-for="s in seriesOrdered"
                        :key="'eb-' + c.key + '-' + s.key"
                        class="text-center p-2 whitespace-nowrap min-w-[8rem]"
                      >
                        {{ seriesHeaderLabel(s) }}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr
                      v-for="(wk, wi) in weekKeysForEdit"
                      :key="`${c.key}-eb-w-${wk}`"
                      class="border-t"
                    >
                      <td class="p-2 font-medium sticky left-0 bg-background whitespace-nowrap z-10">
                        {{ weekColumnLabels[wi] }}
                      </td>
                      <td v-for="s in seriesOrdered" :key="`${c.key}-${wk}-${s.key}`" class="p-2 align-top text-center">
                        <div class="json-kpi-week-input-wrap">
                          <BaseInput
                            :value="String(getWeekValue(s.key, c.key, wk))"
                            className="text-center tabular-nums px-2"
                            @input="(v) => setWeekValue(s.key, c.key, wk, v)"
                          />
                        </div>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          <div v-else class="overflow-auto border rounded-lg">
            <table class="min-w-full text-sm">
              <thead>
                <tr class="bg-muted/30">
                  <th class="text-left p-3 sticky left-0 bg-muted/30">Mês</th>
                  <th v-for="s in seriesOrdered" :key="s.key" class="text-left p-3 whitespace-nowrap">
                    {{ seriesHeaderLabel(s) }}
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="c in categories" :key="c.key" class="border-t">
                  <td class="p-3 font-medium sticky left-0 bg-background whitespace-nowrap">{{ c.label }}</td>
                  <td v-for="s in seriesOrdered" :key="`${c.key}-${s.key}`" class="p-2">
                    <BaseInput
                      :value="String(getValue(s.key, c.key))"
                      :className="'min-w-[8rem]'"
                      @input="(v) => setValue(s.key, c.key, v)"
                    />
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <div class="flex justify-end gap-2">
            <BaseButton variant="outline" @click="isOpen = false">Cancelar</BaseButton>
            <BaseButton @click="handleSave">Salvar</BaseButton>
          </div>
        </div>
      </template>
    </BaseDialog>

    <BaseDialog
      :open.sync="isChartZoomOpen"
      contentClass="overflow-hidden flex flex-col"
      :contentStyle="chartZoomContentStyle"
    >
      <template #header>
        <div class="flex flex-col gap-1 pr-8">
          <div class="text-lg leading-none font-semibold">{{ titleComputed }}</div>
          <div class="text-xs text-muted-foreground">Visualização ampliada</div>
        </div>
      </template>
      <div
        v-if="isChartZoomOpen"
        class="relative w-full h-[70vh] min-h-[20rem] sm:h-[76vh] md:h-[80vh]"
      >
        <ComboBarLineDualAxisChart
          v-if="isComboBarLineDualAxis"
          :key="'zoom-combo-' + chartZoomKey"
          :chartJs="chartJs"
        />
        <VerticalGroupedBarChart
          v-else-if="isWeeklyVerticalChart"
          :key="'zoom-vbar-' + chartZoomKey"
          :chartJs="chartJs"
        />
        <HorizontalGroupedBarChart
          v-else
          :key="'zoom-hbar-' + chartZoomKey"
          :chartJs="chartJs"
        />
      </div>
    </BaseDialog>
  </BaseCard>
</template>

<script>
import BaseCard from '@/components/ui/BaseCard.vue'
import BaseButton from '@/components/ui/BaseButton.vue'
import BaseDialog from '@/components/ui/BaseDialog.vue'
import BaseInput from '@/components/ui/BaseInput.vue'

import HorizontalGroupedBarChart from '@/components/charts/HorizontalGroupedBarChart'
import VerticalGroupedBarChart from '@/components/charts/VerticalGroupedBarChart'
import ComboBarLineDualAxisChart from '@/components/charts/ComboBarLineDualAxisChart'
import { listDashboardKpiCharts, upsertDashboardKpiChart } from '@/services/dashboardKpiCharts'
import { payloadToHorizontalGroupedBarChartjs } from '@/lib/dashboard-kpi-charts/payloadToChartjsV1'
import { payloadToComboBarLineDualAxisChartjs } from '@/lib/dashboard-kpi-charts/payloadToComboBarLineDualAxis'
import { payloadToConversationsByCollaboratorWeeklyChartjs } from '@/lib/dashboard-kpi-charts/payloadToConversationsByCollaboratorWeekly'

export default {
  name: 'JsonKpiChartCard',
  components: {
    BaseCard,
    BaseButton,
    BaseDialog,
    BaseInput,
    HorizontalGroupedBarChart,
    VerticalGroupedBarChart,
    ComboBarLineDualAxisChart,
  },
  props: {
    year: { type: Number, default: () => new Date().getFullYear() },
    sector: { type: String, required: true },
    kpiName: { type: String, required: true },
    readonly: { type: Boolean, default: false },
    /** Gráfico combo (eixo duplo): mais altura e sem clip do card nos rótulos % */
    tallChart: { type: Boolean, default: false },
  },
  data() {
    return {
      isLoading: false,
      error: '',
      isOpen: false,
      isChartZoomOpen: false,
      chartZoomKey: 0,
      payload: null,
      localPayload: null,
    }
  },
  computed: {
    useTallChart() {
      return this.tallChart || this.isComboBarLineDualAxis || this.isWeeklyVerticalChart
    },
    tallChartCardClass() {
      return this.useTallChart ? '!overflow-visible' : ''
    },
    chartOuterClass() {
      return this.useTallChart
        ? 'relative w-full min-h-96 h-96 sm:min-h-[26rem] sm:h-[26rem] md:min-h-[28rem] md:h-[28rem]'
        : 'relative w-full h-72 sm:h-80 md:h-96'
    },
    titleComputed() {
      return this.payload?.title || this.kpiName
    },
    categories() {
      return Array.isArray(this.localPayload?.categories) ? this.localPayload.categories : []
    },
    seriesOrdered() {
      const s = Array.isArray(this.localPayload?.series) ? this.localPayload.series : []
      return [...s].sort((a, b) => Number(a?.order ?? 0) - Number(b?.order ?? 0))
    },
    activePayload() {
      return this.payload || this.localPayload || {}
    },
    /** Normalizado para comparações estáveis (conversas / reembolsos / combo). */
    chartTypeId() {
      return String(this.activePayload?.chartType ?? '').trim()
    },
    isComboBarLineDualAxis() {
      return this.chartTypeId === 'comboBarLineDualAxis'
    },
    /** Modal colaborador × semanas (compras / qtd_conversas). Independente de isWeeklyVerticalChart. */
    isConversationsWeekly() {
      return this.chartTypeId === 'conversationsByCollaboratorWeekly'
    },
    isReembolsosWeekly() {
      return this.chartTypeId === 'reembolsosSolicitadosRevertidosWeekly'
    },
    /** E-books, Livros e Antecedência (operações): combo + 4 semanas/mês. */
    isOperacoesComboWeekly() {
      const kpis = [
        'ebooks_operacoes',
        'livro_operacoes',
        'antecedencia_livros_ebooks_menor_30',
        'antecedencia_livros_ebooks_maior_30',
      ]
      return (
        this.sector === 'operacoes' &&
        kpis.includes(this.kpiName) &&
        this.chartTypeId === 'comboBarLineDualAxis' &&
        Array.isArray(this.localPayload?.weekKeys) &&
        this.localPayload.weekKeys.length > 0
      )
    },
    isWeeklyVerticalChart() {
      return this.isConversationsWeekly || this.isReembolsosWeekly
    },
    chartJs() {
      if (this.isComboBarLineDualAxis) {
        return payloadToComboBarLineDualAxisChartjs(this.activePayload)
      }
      if (this.isWeeklyVerticalChart) {
        return payloadToConversationsByCollaboratorWeeklyChartjs(this.activePayload)
      }
      return payloadToHorizontalGroupedBarChartjs(this.activePayload)
    },
    editButtonLabel() {
      if (this.isConversationsWeekly) return 'Atribuir resultados'
      if (this.isReembolsosWeekly || this.isOperacoesComboWeekly) return 'Inserir resultados'
      return 'Editar'
    },
    modalSubtitle() {
      if (this.isConversationsWeekly) {
        return 'Informe a quantidade de conversas por colaborador e por semana.'
      }
      if (this.isReembolsosWeekly) {
        return 'Informe por mês e por semana: reembolsos solicitados e revertidos.'
      }
      if (this.isOperacoesComboWeekly) {
        if (
          this.kpiName === 'antecedencia_livros_ebooks_menor_30' ||
          this.kpiName === 'antecedencia_livros_ebooks_maior_30'
        ) {
          return 'Por mês, informe os valores semanais em dias (4 semanas). No gráfico, cada indicador no mês é a média das 4 semanas.'
        }
        return 'Por mês, informe os valores semanais em dias. No gráfico, meta e as médias usam a média das 4 semanas.'
      }
      return 'Editar valores'
    },
    weekKeysForEdit() {
      const w = this.localPayload?.weekKeys
      return Array.isArray(w) && w.length ? w.map(String) : ['1', '2', '3', '4']
    },
    weekColumnLabels() {
      const keys = this.weekKeysForEdit
      const labels = this.localPayload?.weekLabels
      if (Array.isArray(labels)) {
        return keys.map((k, i) => String(labels[i] ?? `Sem. ${k}`))
      }
      return keys.map((k) => `Sem. ${k}`)
    },
    /** Modal: largura maior para matrizes semanais (conversas ou reembolsos). */
    dialogContentStyle() {
      // Operações (livros/e-books): tabela semanal fica larga (4 colunas + inputs).
      // Aumenta o modal para evitar scroll horizontal.
      if (this.isOperacoesComboWeekly) {
        return {
          maxHeight: '85vh',
          width: 'min(96vw, 72rem)',
          maxWidth: 'min(96vw, 72rem)',
        }
      }
      if (this.isWeeklyVerticalChart) {
        return {
          maxHeight: '85vh',
          width: 'min(96vw, 40rem)',
          maxWidth: 'min(96vw, 40rem)',
        }
      }
      return {
        maxHeight: '85vh',
        width: 'min(67.2vw, 61.6rem)',
        maxWidth: 'min(67.2vw, 61.6rem)',
      }
    },
    chartZoomContentStyle() {
      return {
        maxHeight: '92vh',
        width: 'min(96vw, 96rem)',
        maxWidth: 'min(96vw, 96rem)',
      }
    },
  },
  mounted() {
    this.fetch()
  },
  methods: {
    openChartZoom() {
      this.chartZoomKey += 1
      this.isChartZoomOpen = true
    },
    normalizeAntecedenciaSeriesLabels(p) {
      if (!p || !Array.isArray(p.series)) return
      for (const ser of p.series) {
        if (ser.key === 'media_antecedencia_inscricao_turma') ser.label = 'Antecedência(média)'
        if (ser.key === 'media_prazo_envio_pos_inscricao') ser.label = 'Prazo(média)'
      }
    },
    seriesHeaderLabel(s) {
      const label = String(s?.label ?? s?.key ?? '')
      const fmt = String(s?.format ?? '')
      if (fmt === 'percentage') return `${label} (%)`
      if (fmt === 'currency') return `${label} (R$)`
      if (fmt === 'days') {
        if (
          this.kpiName === 'antecedencia_livros_ebooks_menor_30' ||
          this.kpiName === 'antecedencia_livros_ebooks_maior_30'
        ) {
          return label
        }
        return `${label} (dias)`
      }
      return label
    },
    async fetch() {
      this.isLoading = true
      this.error = ''
      try {
        const rows = await listDashboardKpiCharts({ year: this.year, sector: this.sector, kpi_name: this.kpiName })
        const row = Array.isArray(rows) ? rows[0] : rows
        this.payload = row?.payload || null
        this.localPayload = this.payload ? JSON.parse(JSON.stringify(this.payload)) : null
        if (
          this.kpiName === 'antecedencia_livros_ebooks_menor_30' ||
          this.kpiName === 'antecedencia_livros_ebooks_maior_30'
        ) {
          this.normalizeAntecedenciaSeriesLabels(this.localPayload)
          this.normalizeAntecedenciaSeriesLabels(this.payload)
        }
      } catch (e) {
        this.error = 'Falha ao carregar o gráfico.'
      } finally {
        this.isLoading = false
      }
    },
    getValue(seriesKey, categoryKey) {
      const values = this.localPayload?.values || {}
      const row = values?.[seriesKey] || {}
      const v = row?.[categoryKey]
      return Number(v ?? 0)
    },
    setValue(seriesKey, categoryKey, raw) {
      if (!this.localPayload) return
      const s = String(raw ?? '').trim()
      const n = s === '' ? 0 : Number.parseFloat(s.replace(',', '.'))
      const value = Number.isNaN(n) ? 0 : n

      if (!this.localPayload.values) this.$set(this.localPayload, 'values', {})
      if (!this.localPayload.values[seriesKey]) this.$set(this.localPayload.values, seriesKey, {})
      this.$set(this.localPayload.values[seriesKey], categoryKey, value)
    },
    getWeekValue(seriesKey, monthKey, weekKey) {
      const v = this.localPayload?.values?.[seriesKey]?.[monthKey]?.[weekKey]
      return Number(v ?? 0)
    },
    setWeekValue(seriesKey, monthKey, weekKey, raw) {
      if (!this.localPayload) return
      const s = String(raw ?? '').trim()
      const n = s === '' ? 0 : Number.parseFloat(s.replace(',', '.'))
      let value = Number.isNaN(n) ? 0 : n
      if (this.isOperacoesComboWeekly) {
        value = Math.max(0, value)
        value = Math.round(value * 100) / 100
      } else {
        value = Math.max(0, Math.round(value))
      }

      if (!this.localPayload.values) this.$set(this.localPayload, 'values', {})
      if (!this.localPayload.values[seriesKey]) this.$set(this.localPayload.values, seriesKey, {})
      if (!this.localPayload.values[seriesKey][monthKey]) {
        this.$set(this.localPayload.values[seriesKey], monthKey, {})
      }
      this.$set(this.localPayload.values[seriesKey][monthKey], weekKey, value)
    },
    async handleSave() {
      if (!this.localPayload) return
      try {
        const saved = await upsertDashboardKpiChart({
          year: this.year,
          sector: this.sector,
          kpi_name: this.kpiName,
          payload: this.localPayload,
        })
        this.payload = saved?.payload || this.localPayload
        this.localPayload = this.payload ? JSON.parse(JSON.stringify(this.payload)) : null
        this.isOpen = false
      } catch (e) {
        this.error = 'Falha ao salvar o gráfico.'
      }
    },
  },
}
</script>

<style scoped>
/* Largura fixa do input semanal (2× o bloco inicial de ~2.75rem). */
.json-kpi-week-input-wrap {
  width: 6.25rem;
  max-width: 100%;
  box-sizing: border-box;
  margin-left: auto;
  margin-right: auto;
}
</style>

